import { Component, ReactNode } from 'react';

// ==============================|| DOM PROTECTION ||============================== //
// This component prevents third-party scripts (like Crisp chat, Google Translate, browser extensions)
// from causing React DOM manipulation errors like "removeChild" or "insertBefore" failures.

interface DOMProtectionProps {
  children: ReactNode;
}

interface DOMProtectionState {
  hasError: boolean;
  errorCount: number;
}

/**
 * DOMProtection wraps the application to catch and handle DOM manipulation errors
 * that can occur when third-party scripts (chat widgets, browser extensions, etc.)
 * modify the DOM that React is managing.
 * 
 * Common errors this handles:
 * - "The node to be removed is not a child of this node"
 * - "Failed to execute 'removeChild' on 'Node'"
 * - "Failed to execute 'insertBefore' on 'Node'"
 */
class DOMProtection extends Component<DOMProtectionProps, DOMProtectionState> {
  constructor(props: DOMProtectionProps) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<DOMProtectionState> | null {
    // Check if this is a DOM manipulation error from third-party scripts
    if (isDOMManipulationError(error)) {
      return { hasError: true };
    }
    // Let other errors propagate normally
    return null;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isDOMManipulationError(error)) {
      console.warn(
        'DOMProtection: Caught DOM manipulation error (likely from third-party script):',
        error.message
      );
      
      // Increment error count and attempt recovery
      this.setState((prevState) => ({
        errorCount: prevState.errorCount + 1
      }));

      // Auto-recover after a brief delay (React will re-render)
      setTimeout(() => {
        this.setState({ hasError: false });
      }, 100);
    } else {
      // Log other errors but don't swallow them
      console.error('DOMProtection: Caught unexpected error:', error, errorInfo);
    }
  }

  componentDidMount() {
    // Add global error handler to catch unhandled DOM errors
    window.addEventListener('error', this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
  }

  handleGlobalError = (event: ErrorEvent) => {
    if (isDOMManipulationError(event.error)) {
      // Prevent the error from showing in console as uncaught
      event.preventDefault();
      console.warn('DOMProtection: Suppressed global DOM manipulation error');
    }
  };

  render() {
    // Even if there's an error, try to render children (React will reconcile)
    // This is safe for DOM manipulation errors since React's virtual DOM is still intact
    return this.props.children;
  }
}

/**
 * Check if an error is a DOM manipulation error typically caused by
 * third-party scripts modifying React's managed DOM
 */
function isDOMManipulationError(error: Error | null | undefined): boolean {
  if (!error || !error.message) return false;
  
  const errorMessage = error.message.toLowerCase();
  const domErrorPatterns = [
    'removechild',
    'insertbefore',
    'appendchild',
    'replacechild',
    'not a child of this node',
    'not a sub-node of this node',
    'the node to be removed',
    'failed to execute'
  ];
  
  return domErrorPatterns.some((pattern) => errorMessage.includes(pattern));
}

export default DOMProtection;
export { isDOMManipulationError };
